import { apiGet, apiSend } from './client';

export type BonusType = {
  id: string;
  key: string;
  label: string;
  sortOrder: number;
};

export function displayBonusLabel(bonus: { key?: string | null; label?: string | null } | null | undefined) {
  if (!bonus) {
    return '';
  }

  if (bonus.key === 'tradeskill') {
    return 'Skill';
  }

  if ((bonus.label ?? '').trim().toLowerCase() === 'tradeskill') {
    return 'Skill';
  }

  return bonus.label ?? '';
}

export type BonusZone = {
  id: string;
  serverId: number;
  slug: string;
  name: string;
  expansion: string | null;
  minLevel: number | null;
  maxLevel: number | null;
  zoneType: string | null;
  legacyZoneId: number | null;
  isActive: boolean;
};

export type TodayBonusRow = {
  id: string | null;
  zoneId: string;
  zoneSlug: string;
  name: string;
  expansion: string | null;
  minLevel: number | null;
  maxLevel: number | null;
  zoneType: string | null;
  bonusTypeId: string | null;
  bonus: string;
  bonusLabel: string;
  state: 'ACTIVE' | 'PENDING_REMOVAL' | 'DISPUTED' | 'REMOVED' | 'UNCONFIRMED';
  bonusDate: string;
  pendingRemovalBy: string | null;
};


export type ZoneTrendRow = {
  id: string;
  slug: string;
  name: string;
  expansion: string | null;
  minLevel: number | null;
  maxLevel: number | null;
  zoneType: string | null;
  counts: Record<string, number>;
  total: number;
};

export type ZoneTrendsResponse = {
  bonusTypes: BonusType[];
  zones: ZoneTrendRow[];
};

export type BonusLogRow = {
  id: string;
  actionType: string;
  reason: string | null;
  createdAt: string;
  isReportable: boolean;
  actor?: { id?: string; username: string; role: string } | null;
  zone?: { name: string } | null;
  bonusType?: { key: string; label: string } | null;
};

export type ZoneHistoryResponse = {
  zone: BonusZone;
  history: Array<{
    id: string;
    bonusDate: string;
    bonus: string;
    bonusLabel: string;
    state: string;
  }>;
  stats: Array<{
    bonusTypeId: string;
    bonus: string;
    bonusLabel: string;
    count: number;
  }>;
};

export function getBonusTypes(serverSlug: string) {
  return apiGet<BonusType[]>(`/servers/${serverSlug}/bonuses/types`);
}

export function getBonusZones(serverSlug: string) {
  return apiGet<BonusZone[]>(`/servers/${serverSlug}/bonuses/zones`);
}

export function getTodayBonuses(serverSlug: string) {
  return apiGet<TodayBonusRow[]>(`/servers/${serverSlug}/bonuses/today`);
}

export function getBonusesForDate(serverSlug: string, date: string) {
  return apiGet<TodayBonusRow[]>(`/servers/${serverSlug}/bonuses/history/date?date=${encodeURIComponent(date)}`);
}

export function getBonusLogs(serverSlug: string, date: string) {
  return apiGet<BonusLogRow[]>(`/servers/${serverSlug}/bonuses/history/logs?date=${encodeURIComponent(date)}`);
}

export function getZoneBonusHistory(serverSlug: string, zoneSlug: string) {
  return apiGet<ZoneHistoryResponse>(`/servers/${serverSlug}/bonuses/zones/${zoneSlug}/history`);
}

export function getZoneBonusTrends(serverSlug: string, filters?: { expansions?: string[]; startDate?: string; endDate?: string }) {
  const params = new URLSearchParams();
  for (const expansion of filters?.expansions ?? []) {
    if (expansion.trim()) params.append('expansion', expansion.trim());
  }
  if (filters?.startDate?.trim()) params.set('startDate', filters.startDate.trim());
  if (filters?.endDate?.trim()) params.set('endDate', filters.endDate.trim());
  const query = params.toString();
  return apiGet<ZoneTrendsResponse>(`/servers/${serverSlug}/bonuses/zones/trends${query ? `?${query}` : ''}`);
}

export function submitBonus(serverSlug: string, zoneId: string, bonusTypeId: string) {
  return apiSend(`/servers/${serverSlug}/bonuses`, 'POST', { zoneId, bonusTypeId });
}

export function removeBonus(serverSlug: string, bonusId: string) {
  return apiSend(`/servers/${serverSlug}/bonuses/${bonusId}/remove`, 'POST');
}

export function reportBonusAction(serverSlug: string, actionId: string, reason?: string) {
  return apiSend(`/servers/${serverSlug}/bonuses/actions/${actionId}/report`, 'POST', { reason });
}
